import {
    Button,
    Link,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { Archive, Check, Repeat, Slash } from 'react-feather'
import { NavLink } from 'react-router-dom'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { ConnectCompanyDialog } from './ConnectCompanyDialog'
import { OutlinedIconButton } from './OutlinedIconButton'
import PRInputsApi from '../../../api/PRInputsApi'
import { PRInputEntity } from 'types/PRInput'
import { CompanyEntity } from 'types/CompanyEntity'

export type PRTableProps = {
    onArchive?: (pr: PRInputEntity) => Promise<void>
    onBlock?: (pr: PRInputEntity) => Promise<void>
    onUnarchive?: (pr: PRInputEntity) => void
    prs: PRInputEntity[] | null
    selectedPR?: PRInputEntity | null
    setSelectedCompany?: (company: Pick<CompanyEntity, 'id' | 'name'>) => void
    setSelectedPR?: (pr: PRInputEntity | null) => void
}

export const PRTable = ({
    prs,
    onArchive,
    onUnarchive,
    onBlock,
    setSelectedCompany: selectTargetCompany,
    selectedPR,
    setSelectedPR,
}: PRTableProps) => {
    const [openConnectCompanyDialog, setOpenConnectCompanyDialog] = useState(false)
    const [pathToConnect, setPathToConnect] = React.useState<string | null>(null)
    const prinputsApi = new PRInputsApi()

    const onSelectPR =
        setSelectedPR &&
        selectTargetCompany &&
        ((pr: PRInputEntity) => {
            // select the PR
            setSelectedPR(pr)

            // if the PR has a company, select the company
            if (pr.companyId) {
                selectTargetCompany({ id: pr.companyId, name: pr.companyName })
            } else {
                // if the PR has no company, open a dialog to select a company
                openConnectDialog(pr)
            }
        })

    const onCancelConnectDialog =
        setSelectedPR &&
        (() => {
            setSelectedPR(null)
        })

    const openConnectDialog = (pr: { url: string }) => {
        setPathToConnect(pr.url)
        setOpenConnectCompanyDialog(true)
    }

    const onConnectCompany =
        selectTargetCompany &&
        (async (path: string, company: Pick<CompanyEntity, 'id' | 'name'>) => {
            await prinputsApi.connectCompanyToPR(path, company.id)
            selectTargetCompany(company)
        })

    if (prs === null || prs === undefined) {
        return (
            <>
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
            </>
        )
    }
    if (prs.length === 0) {
        return <Typography variant='body1'>Inga pressmeddelanden hittades</Typography>
    }

    const rows = prs.map((pr) => (
        <TableRow key={pr.url}>
            <TableCell sx={{ backgroundColor: pr.url === selectedPR?.url ? 'yellow' : null }}>
                <Stack spacing={2}>
                    <Typography variant='caption'>
                        {formatDateYYYYMMDD(pr.publishDate)} {pr.companyName}
                    </Typography>
                    <Link component={NavLink} to={pr.url} target='_blank'>
                        {pr.title || pr.url || 'Titel saknas'}
                    </Link>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack spacing={2}>
                    {onSelectPR && (
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={() => onSelectPR(pr)}
                            endIcon={<Check size={16} />}
                        >
                            Välj
                        </Button>
                    )}
                    {onArchive && (
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            onClick={() => void onArchive(pr)}
                            endIcon={<Archive size={16} />}
                        >
                            Arkiv
                        </Button>
                    )}
                    {onBlock && (
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            onClick={() => void onBlock(pr)}
                            endIcon={<Slash size={16} />}
                        >
                            Block
                        </Button>
                    )}

                    {onUnarchive && (
                        <Tooltip title='Återställ'>
                            <OutlinedIconButton color='primary' size='small' onClick={() => onUnarchive(pr)}>
                                <Repeat size={16} />
                            </OutlinedIconButton>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    ))

    return (
        <>
            {onConnectCompany && onCancelConnectDialog && (
                <ConnectCompanyDialog
                    open={openConnectCompanyDialog}
                    setOpen={setOpenConnectCompanyDialog}
                    pathToConnect={pathToConnect as string}
                    onConnectCompany={onConnectCompany}
                    onCancel={onCancelConnectDialog}
                />
            )}
            <TableContainer>
                <Table>
                    <TableBody>{rows}</TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
